import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Bike, CheckCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BikerApplication = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehiclePlateNumber: '',
    vehicleColor: '',
    vehicleType: 'motorbike'
  });

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      toast({
        title: 'Account Required',
        description: 'Please create an account or login to apply as a biker',
        variant: 'destructive',
      });
      // Store the return URL so we can come back after signup
      sessionStorage.setItem('redirect_after_login', '/biker/apply');
      navigate('/signup');
      return;
    }

    checkExistingApplication();
  }, []);

  const checkExistingApplication = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/drivers/my-profile');
      
      if (response.data.driver) {
        navigate('/biker/dashboard');
      }
    } catch (error) {
      console.log('No existing application');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const api = (await import('../../utils/api')).default;
      
      const applicationData = {
        ...formData,
        phone: `+260${formData.phone.replace(/\D/g, '')}`,
        vehicleYear: parseInt(formData.vehicleYear)
      };

      await api.post('/bikers/apply', applicationData);

      setSubmitted(true);
      toast({
        title: 'Application Submitted!',
        description: 'Your biker application has been submitted for review.',
      });

      setTimeout(() => {
        navigate('/biker/dashboard');
      }, 3000);
    } catch (error) {
      console.error('Application error:', error);
      toast({
        title: 'Application Failed',
        description: error.response?.data?.detail || 'Failed to submit application',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
          <p className="text-gray-600 mb-4">
            Your biker application is being reviewed. You'll be notified once approved.
          </p>
          <p className="text-sm text-gray-500">
            Redirecting to dashboard...
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 py-12 px-4">
      <Card className="max-w-3xl mx-auto p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-full mb-4">
            <Bike className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Apply to Bike</h1>
          <p className="text-gray-600">Join Tekstar as a delivery partner</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm font-medium" data-testid="phone-prefix">+260</span>
                  <Input
                    id="phone"
                    type="tel"
                    className="rounded-l-none"
                    placeholder="XXX XXX XXX"
                    maxLength={9}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                    required
                    data-testid="phone-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Motorbike Information */}
          <div className="pt-6 border-t border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Motorbike Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vehicleMake">Bike Make *</Label>
                <Input
                  id="vehicleMake"
                  placeholder="e.g., Honda, Yamaha"
                  value={formData.vehicleMake}
                  onChange={(e) => setFormData({ ...formData, vehicleMake: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vehicleModel">Bike Model *</Label>
                <Input
                  id="vehicleModel"
                  placeholder="e.g., CG 125"
                  value={formData.vehicleModel}
                  onChange={(e) => setFormData({ ...formData, vehicleModel: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vehicleYear">Year *</Label>
                <Input
                  id="vehicleYear"
                  type="number"
                  min="2000"
                  max="2025"
                  value={formData.vehicleYear}
                  onChange={(e) => setFormData({ ...formData, vehicleYear: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vehiclePlateNumber">Plate Number *</Label>
                <Input
                  id="vehiclePlateNumber"
                  placeholder="e.g., BAZ 1234"
                  value={formData.vehiclePlateNumber}
                  onChange={(e) => setFormData({ ...formData, vehiclePlateNumber: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vehicleColor">Bike Color *</Label>
                <Input
                  id="vehicleColor"
                  placeholder="e.g., Black"
                  value={formData.vehicleColor}
                  onChange={(e) => setFormData({ ...formData, vehicleColor: e.target.value })}
                  required
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white py-6 text-lg font-semibold"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Application'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default BikerApplication;
